import {
  Box,
  Button,
  Grid,
  MenuItem,
  Menu,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import youtubeIcon from "src/assets/svg-icons/aggregators/youtube.svg";
import InfoIcon from "src/assets/svg-icons/info.svg";
import CalculatorIcon from "src/assets/svg-icons/calculator.svg";
import arrowDownIcon from "src/assets/svg-icons/home/down-arrow.svg";
import changeLanguageIcon from "src/assets/svg-icons/home/change-language.svg";
import englandIcon from "src/assets/svg-icons/flags/england.svg";
import saudiIcon from "src/assets/svg-icons/flags/saudi.svg";
import { useTranslation } from "react-i18next";
import { setLocalStoredData } from "src/utils/_localStorage";
import { ConstLocalStorage } from "src/constants/_localStorage";
import CustomListListItem, {
  IListItemProps,
} from "src/shared/components/list-item";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useAddPhone } from "src/api/aggreagators";
import { Helmet } from "react-helmet-async";

const schema = yup.object().shape({
  aggSales: yup.number().required("required"),
  aggRate: yup.number().required("required"),
  deliveryCost: yup.number().required("required"),
  cogs: yup.number().required("required"),
  aggOrders: yup.number().required("required"),
  restaurantSales: yup.number().required("required"),
  opCost: yup.number().required("required"),
  phone: yup
    .string()
    .required("required")
    .matches(/^(05\d{8}|5\d{8})$/, "Invalid Saudi phone number"),
  percentage: yup.number(),
});

const TAX_DIVISOR = 1.15;
const BANK_FEE_PERCENTAGE = 2.5;
const COMPENSATION_PERCENTAGE = 2;

export interface FormInputs extends yup.InferType<typeof schema> {}

const AggregatorsCalculator = () => {
  const { t, i18n } = useTranslation("calculator");
  const { t: aggregatorsT } = useTranslation("aggregators");

  const { mutate } = useAddPhone();

  const {
    formState: { errors },
    getValues,
    watch,
    control,
    handleSubmit,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      percentage: 0,
    },
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorE2, setAnchorE2] = useState<null | HTMLElement>(null);

  const openMenuE2 = Boolean(anchorE2);

  const handleLanguageChange = (language: "en" | "ar") => {
    i18n.changeLanguage(language);
    setLocalStoredData(ConstLocalStorage.language, language);
    setAnchorEl(null);
    window.location.reload();
  };

  const handleClickE2 = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorE2(event.currentTarget);
  };

  const handleCloseE2 = () => {
    setAnchorE2(null);
  };

  const aggOrders = watch("aggOrders");

  const [calculations, setCalculations] = useState({
    netSales: 0,
    cogs: 0,
    aggFee: 0,
    deliveryFee: 0,
    bankFee: 0,
    compensation: 0,
    totalCost: 0,
    grossProfit: 0,
    grossProfitPercentage: 0,
    totalOC: 0,
    netProfit: 0,
    netProfitPercentage: 0,
  });

  const {
    netSales,
    cogs,
    aggFee,
    deliveryFee,
    bankFee,
    compensation,
    totalCost,
    grossProfit,
    grossProfitPercentage,
    totalOC,
    netProfit,
    netProfitPercentage,
  } = calculations;

  const listData: IListItemProps[] = [
    {
      label: aggregatorsT("netSales"),
      value: netSales.toFixed(2),
      icon: "show",
      iconColor: "black",
    },
    {
      label: aggregatorsT("cogs"),
      value: -cogs.toFixed(2),
      icon: "show",
      iconColor: "black",
    },
    {
      label: aggregatorsT("aggFee"),
      value: -aggFee.toFixed(2),
    },
    {
      label: t("deliveryFee"),
      value: -deliveryFee.toFixed(2),
    },
    {
      label: aggregatorsT("bankFee"),
      value: -bankFee.toFixed(2),
    },
    {
      label: aggregatorsT("compensation"),
      value: -compensation.toFixed(2),
    },
    {
      label: aggregatorsT("otherFees"),
      value: "0",
    },
    {
      label: aggregatorsT("totalCosts"),
      value: -totalCost.toFixed(2),
    },
  ];

  const AveragesListData: IListItemProps[] = [
    {
      label: t("avgOrders"),
      value: (netSales / aggOrders).toFixed(2),
      icon: "show",
      iconColor: "black",
    },
    {
      label: aggregatorsT("avgCogs"),
      value: (-cogs / aggOrders).toFixed(2),
      icon: "show",
      iconColor: "black",
    },
    {
      label: t("avgAggregatorFee"),
      value: (-aggFee / aggOrders).toFixed(2),
    },
    {
      label: t("avgDeliveryFee"),
      value: (-deliveryFee / aggOrders).toFixed(2),
    },
    {
      label: t("avgBankFee"),
      value: (-bankFee / aggOrders).toFixed(2),
    },
    {
      label: t("avgCompensation"),
      value: (-compensation / aggOrders).toFixed(2),
    },
    {
      label: t("avgOtherFee"),
      value: "0",
    },

    {
      label: t("avgTotalCosts"),
      value: (-totalCost / aggOrders).toFixed(2),
    },
  ];

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    console.log({ data });
    const {
      aggSales,
      aggRate,
      aggOrders,
      deliveryCost,
      cogs,
      restaurantSales,
      opCost,
      percentage,
      phone,
    } = data;

    // setNetSales((prev) => aggSales);
    // setCogs((prev) => (netSales * cogs) / 100);
    // setAggFee((prev) => (netSales * aggRate) / 100);
    // setDeliveryFee((prev) => (deliveryCost * aggOrders) / 1.15);
    // setBankFee((prev) => (netSales * 2.5) / 100);
    // setCompensation((prev) => (netSales * 2) / 100);
    const calculatedNetSales = aggSales * (1 + (percentage ?? 0) / 100);
    const calculatedCogs = aggSales * (1 / 100) * cogs;
    const calculatedAggFee = (calculatedNetSales * aggRate) / 100;
    const calculatedDeliveryFee = (deliveryCost * aggOrders) / TAX_DIVISOR;
    const calculatedBankFee = (calculatedNetSales * BANK_FEE_PERCENTAGE) / 100;
    const calculatedCompensation =
      (calculatedNetSales * COMPENSATION_PERCENTAGE) / 100;

    const calculatedTotalCost =
      calculatedCogs +
      calculatedAggFee +
      calculatedDeliveryFee +
      calculatedBankFee +
      calculatedCompensation;

    const calculatedGrossProfit = calculatedNetSales - calculatedTotalCost;
    const calculatedGrossProfitPercentage =
      (calculatedGrossProfit / calculatedNetSales) * 100;
    const calculatedTotalOC = opCost * (calculatedNetSales / restaurantSales);
    const calculatedNetsProfit = calculatedGrossProfit - calculatedTotalOC;
    const calculatedNetProfitPercentage =
      (calculatedNetsProfit / calculatedNetSales) * 100;

    const calculatedValues = {
      netSales: calculatedNetSales,
      cogs: calculatedCogs,
      aggFee: calculatedAggFee,
      deliveryFee: calculatedDeliveryFee,
      bankFee: calculatedBankFee,
      compensation: calculatedCompensation,
      totalCost: calculatedTotalCost,
      grossProfit: calculatedGrossProfit,
      grossProfitPercentage: calculatedGrossProfitPercentage,
      totalOC: calculatedTotalOC,
      netProfit: calculatedNetsProfit,
      netProfitPercentage: calculatedNetProfitPercentage,
    };

    setCalculations(calculatedValues);

    mutate({ phone: phone });
  };

  return (
    <Stack spacing={3} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      <Helmet>
        <title>Aggregator Calculator from JAZR Platform</title>
        <meta
          name="description"
          content="jazr App Calculator for calculating hidden aggregators costs and all fees: Jahez, HungerStation, ToYou, ketta , Mrsool."
          lang="en"
        />
        <meta
          name="description"
          content="حاسبة التطبيقات من جزر لحساب تكاليف التطبيقات الخفيه وجميع الرسوم  جاهز , هنقر ستيشن, تويو, كيتا, مرسول"
          lang="ar"
        />
        <link rel="canonical" href="/aggregator-calc" />
        {/* Head GTM script */}
        <script>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-WB9XJJNB');`}
        </script>
        {/* Google Tag Manager */}
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-PKMQBTY8V1"
        ></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-PKMQBTY8V1');
          `}
        </script>
      </Helmet>

      {/* Noscript GTM iframe */}
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-WB9XJJNB"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        ></iframe>
      </noscript>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          flexWrap={"wrap"}
        >
          <Stack direction={"row"} spacing={"12px"}>
            <Box
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              width={"60px"}
              height={"60px"}
              bgcolor={"#D0D5DD"}
              borderRadius={"16px"}
            >
              <img src={CalculatorIcon} width={"32px"} alt="" />
            </Box>
            <Box>
              <Typography fontSize={"32px"} lineHeight={"38.4px"}>
                {t("calculator")}
              </Typography>
              <Typography
                fontSize={"20px"}
                lineHeight={"24px"}
                color={"#98A2B3"}
              >
                {t("description")}
              </Typography>
            </Box>
            <Box>
              <Button variant="text" onClick={handleClickE2} size="small">
                <Stack direction={"row"} spacing={1}>
                  <img src={arrowDownIcon} alt="" />
                  <Typography
                    fontSize={"14px"}
                    fontWeight={500}
                    color={"#334155"}
                    lineHeight={"14px"}
                  >
                    {i18n.language === "en" ? "En" : "Ar"}
                  </Typography>
                  <img src={changeLanguageIcon} alt="" />
                </Stack>
              </Button>
              <Menu
                id="basic-menu"
                anchorEl={anchorE2}
                open={openMenuE2}
                onClose={handleCloseE2}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                <MenuItem
                  onClick={() => handleLanguageChange("en")}
                  sx={{ gap: 1 }}
                >
                  <img src={englandIcon} alt="" />
                  <Typography
                    color={"#1D2939"}
                    fontSize={"16px"}
                    textTransform={"capitalize"}
                  >
                    English
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => handleLanguageChange("ar")}
                  sx={{ gap: 1 }}
                >
                  <img src={saudiIcon} alt="" />
                  <Typography
                    color={"#1D2939"}
                    fontSize={"16px"}
                    textTransform={"capitalize"}
                  >
                    العربية
                  </Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            color="error"
            sx={{
              height: "48px",
              border: "1px solid  #B32318",
              mt: { xs: 2, md: 0 },
            }}
            startIcon={<img src={youtubeIcon} alt="" width={"34px"} />}
            href="https://youtu.be/ExTHtvb0s48"
            target="_blank"
          >
            {t("viewHelp")}
          </Button>
        </Box>
        <Box bgcolor={"#F2F4F7"} width={"100%"} height={"1px"} />
        <Box display={"flex"} justifyContent={"flex-end"} py={1.6}>
          <Stack
            direction={"row"}
            spacing={2}
            alignItems={"center"}
            flexWrap={"wrap"}
          >
            <Typography
              fontSize={{ xs: "15px", lg: "18px" }}
              color={"#101828"}
              lineHeight={"21.6px"}
            >
              {t("raiseMenu")}
            </Typography>

            {/* Percentage select */}
            <Box minWidth={120}>
              {/* <FormControl fullWidth size="small">
                <InputLabel>{t("percentage")}</InputLabel>
                <Select
                  // value={percentage}
                  // onChange={handleChangePercentage}
                  input={<OutlinedInput label={t("percentage")} />}
                >
                  <MenuItem>10%</MenuItem>
                  <MenuItem>20%</MenuItem>
                </Select>
              </FormControl> */}
              <Controller
                name="percentage"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={t("percentage")}
                    size="small"
                    {...field}
                    error={!!errors.percentage}
                    helperText={errors.percentage?.message}
                  />
                )}
              />
            </Box>
            {/* <Switch defaultChecked />
            <Typography
              fontSize={{ xs: "15px", lg: "18px" }}
              color={"#101828"}
              lineHeight={"21.6px"}
            >
              {t("deliveryOffers")}
            </Typography> */}
          </Stack>
        </Box>
        <Grid container spacing={{ xs: 0, lg: 3 }}>
          <Grid item xs={12} lg={2.2}>
            <Typography
              mb={3}
              fontSize={"16px"}
              fontWeight={600}
              lineHeight={"19.2px"}
            >
              {t("inputs")}
            </Typography>
            <Stack spacing={1.5}>
              {/* <TextField
                fullWidth
                id="outlined-basic"
                size="small"
                label={t("monthlyAggSalesExcludeTax")}
                variant="outlined"
              /> */}
              <Controller
                name="aggSales"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={t("monthlyAggSalesExcludeTax")}
                    size="small"
                    {...field}
                    error={!!errors.aggSales}
                    helperText={errors.aggSales?.message}
                  />
                )}
              />
              <Controller
                name="aggRate"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={t("aggregatorRate")}
                    size="small"
                    {...field}
                    error={!!errors.aggRate}
                    helperText={errors.aggRate?.message}
                  />
                )}
              />
              <Controller
                name="deliveryCost"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={t("deliveryCost")}
                    size="small"
                    {...field}
                    error={!!errors.deliveryCost}
                    helperText={errors.deliveryCost?.message}
                  />
                )}
              />
              {/* <TextField
              fullWidth
              id="outlined-basic"
              size="small"
              label={t("averageBill")}
              variant="outlined"
            /> */}
              <Controller
                name="cogs"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={`${t("cogs")} %`}
                    size="small"
                    {...field}
                    error={!!errors.cogs}
                    helperText={errors.cogs?.message}
                  />
                )}
              />
              <Controller
                name="aggOrders"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={t("appOrders")}
                    size="small"
                    {...field}
                    error={!!errors.aggOrders}
                    helperText={errors.aggOrders?.message}
                  />
                )}
              />
              <Controller
                name="restaurantSales"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={t("totalRestaurantSales")}
                    size="small"
                    {...field}
                    error={!!errors.restaurantSales}
                    helperText={errors.restaurantSales?.message}
                  />
                )}
              />
              <Controller
                name="opCost"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={t("totalCosts")}
                    size="small"
                    {...field}
                    error={!!errors.opCost}
                    helperText={errors.opCost?.message}
                  />
                )}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} lg={4.9} sx={{ my: { xs: 2, lg: 0 } }}>
            <Box p={"18px"} borderRadius={"4px"} border={"1px solid #EAECF0"}>
              <Stack
                direction={"row"}
                p={1.5}
                bgcolor={"#F9FAFB"}
                border={"1px solid #EAECF0"}
                borderRadius={1}
                spacing={"10px"}
                mb={3}
              >
                <Typography
                  fontSize={"16px"}
                  color={"#667085"}
                  lineHeight={"19.2px"}
                >
                  {aggregatorsT("totalSales")}
                </Typography>
                <img src={InfoIcon} alt="" width={"16px"} />
              </Stack>
              <Stack spacing={1}>
                {listData.map((item) => (
                  <CustomListListItem
                    label={item.label}
                    value={item.value}
                    icon={item.icon === "show" ? "show" : "hidden"}
                    divider={item.divider === "hidden" ? "hidden" : "show"}
                    iconColor={item.iconColor === "black" ? "black" : "gray"}
                  />
                ))}

                <Box justifyContent={"space-between"} display={"flex"} mb={1}>
                  <Typography
                    fontSize={"16px"}
                    color={"#1D2939"}
                    lineHeight={"16.8px"}
                    fontWeight={700}
                  >
                    {t("grossProfit")}
                  </Typography>
                  <Stack direction={"row"} gap={1}>
                    <Typography
                      fontSize={"16px"}
                      color={"#1D2939"}
                      lineHeight={"16.8px"}
                      fontWeight={700}
                    >
                      ({grossProfitPercentage.toFixed(2)}%)
                    </Typography>
                    <Typography
                      fontSize={"16px"}
                      color={"#1D2939"}
                      lineHeight={"16.8px"}
                    >
                      {grossProfit.toFixed(2)}
                    </Typography>
                  </Stack>
                </Box>
                {/* divider */}
                <Box width="100%" bgcolor="#EAECF0" height="1px" />
                <Box justifyContent={"space-between"} display={"flex"} mb={1}>
                  <Typography
                    fontSize={"14px"}
                    color={"#1D2939"}
                    lineHeight={"16.8px"}
                  >
                    {t("totalOperatingCosts")}
                  </Typography>
                  <Typography
                    fontSize={"16px"}
                    color={"#1D2939"}
                    lineHeight={"16.8px"}
                  >
                    -{totalOC.toFixed(2)}
                  </Typography>
                </Box>
                {/* divider */}
                <Box width="100%" bgcolor="#EAECF0" height="1px" />
                <Box justifyContent={"space-between"} display={"flex"} mb={1}>
                  <Typography
                    fontSize={"16px"}
                    color={"#1D2939"}
                    lineHeight={"16.8px"}
                    fontWeight={700}
                  >
                    {t("netProfit")}
                  </Typography>
                  <Stack direction={"row"} gap={1}>
                    <Typography
                      fontSize={"16px"}
                      color={"#1D2939"}
                      lineHeight={"16.8px"}
                      fontWeight={700}
                    >
                      ({netProfitPercentage.toFixed(2)}%)
                    </Typography>
                    <Typography
                      fontSize={"16px"}
                      color={"#1D2939"}
                      lineHeight={"16.8px"}
                    >
                      {netProfit.toFixed(2)}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4.9}>
            <Box p={"18px"} borderRadius={"4px"} border={"1px solid #EAECF0"}>
              <Stack
                direction={"row"}
                p={1.5}
                bgcolor={"#F9FAFB"}
                border={"1px solid #EAECF0"}
                borderRadius={1}
                spacing={"10px"}
                mb={3}
              >
                <Typography color={"#667085"} lineHeight={"16.8px"}>
                  {t("averages")}
                </Typography>
                <img src={InfoIcon} alt="" width={"16px"} />
              </Stack>
              <Stack spacing={1}>
                {AveragesListData.map((item) => (
                  <CustomListListItem
                    label={item.label}
                    value={item.value}
                    icon={item.icon === "show" ? "show" : "hidden"}
                    divider={item.divider === "hidden" ? "hidden" : "show"}
                    iconColor={item.iconColor === "black" ? "black" : "gray"}
                  />
                ))}

                <Box justifyContent={"space-between"} display={"flex"} mb={1}>
                  <Typography
                    color={"#1D2939"}
                    lineHeight={"16.8px"}
                    fontWeight={700}
                  >
                    {t("avgGrossProfit")}
                  </Typography>
                  <Stack direction={"row"} gap={1}>
                    <Typography
                      color={"#1D2939"}
                      lineHeight={"16.8px"}
                      fontWeight={700}
                    >
                      ({grossProfitPercentage.toFixed(2)}%)
                    </Typography>
                    <Typography color={"#1D2939"} lineHeight={"16.8px"}>
                      {(grossProfit / aggOrders).toFixed(2)}
                    </Typography>
                  </Stack>
                </Box>
                {/* divider */}
                <Box width="100%" bgcolor="#EAECF0" height="1px" />
                <Box justifyContent={"space-between"} display={"flex"} mb={1}>
                  <Typography color={"#1D2939"} lineHeight={"16.8px"}>
                    {t("avgTotalOperatingCosts")}
                  </Typography>
                  <Typography
                    fontSize={"16px"}
                    color={"#1D2939"}
                    lineHeight={"19.2px"}
                  >
                    {(totalOC / aggOrders).toFixed(2)}
                  </Typography>
                </Box>
                {/* divider */}
                <Box width="100%" bgcolor="#EAECF0" height="1px" />
                <Box justifyContent={"space-between"} display={"flex"} mb={1}>
                  <Typography
                    color={"#1D2939"}
                    lineHeight={"16.8px"}
                    fontWeight={700}
                  >
                    {t("argNetProfit")}
                  </Typography>
                  <Stack direction={"row"} gap={1}>
                    <Typography
                      color={"#1D2939"}
                      lineHeight={"19.2px"}
                      fontWeight={700}
                    >
                      ({netProfitPercentage.toFixed(2)}%)
                    </Typography>
                    <Typography color={"#1D2939"} lineHeight={"16.8px"}>
                      {(netProfit / aggOrders).toFixed(2)}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          mt={2}
        >
          <Typography color={"#000"} lineHeight={"16.8px"} fontSize={"14px"}>
            {t("compensationRateVaries")}
          </Typography>
          <Stack spacing={2}>
            {/* <TextField
              fullWidth
              id="outlined-basic"
              size="small"
              label={t("phoneNumber")}
              variant="outlined"
            /> */}
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  fullWidth
                  label={t("phoneNumber")}
                  size="small"
                  {...field}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              )}
            />
            <Button type="submit" variant="contained">
              {t("submit")}
            </Button>
          </Stack>
        </Box>
      </form>
    </Stack>
  );
};

export default AggregatorsCalculator;
